// TODO: Extract gradient border option into another component, e.g. GradientBorderInput.jsx

import useWhiteLabelComponent from '../../../hooks/useWhiteLabelComponent';
import { ThemeType } from '../../../types/styles';
import { InputContainer } from './Input.styles';
import { forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
    /** Function to call whenever user changes the field's value. */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    /** Name of field. Used in `name` and `id` attribute. Must be unique to page. */
    name?: string;
    /** Chosen value is invalid. Sets `aria-invalid` to true and renders red border around element. */
    error?: boolean;
    /** Value of input field. For checkboxes, use checked prop instead. */
    value?: string | number | boolean;
    /** Id of element describing this field. Will be included in `aria-describedby` attribute. */
    descriptionId?: string;
    /** Whether this is a required field. */
    required?: boolean;
    /** Whether the field should be a larger size. */
    large?: boolean;
    /** Whether input field should fill width of container. */
    fullWidth?: boolean;
    /** Set field to narrow width. */
    narrowWidth?: boolean;
    /** Hide spin buttons added by browser. Only relevant if input type is "number". */
    hideSpinButtons?: boolean;
    variant: ThemeType;
}

/**
 * Input field for forms.
 *
 * For accessibility, please always do the following:
 * - Use aria-label if not using a label as part of Form.Input
 * - Use autocomplete attribute whenever possible. List of possible values: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
 */
const Input = forwardRef<HTMLInputElement, InputProps>(({
    onChange,
    name,
    error = false,
    value = '',
    required = false,
    large = false,
    fullWidth = false,
    narrowWidth = false,
    descriptionId,
    hideSpinButtons = false,
    ...props
}, ref) => {

    const { accentColour } = useWhiteLabelComponent() as { accentColour: string };

    return (
        <InputContainer
            ref={ref}
            onChange={onChange}
            name={name}
            id={name}
            value={props.type === 'checkbox' ? undefined : (value as string | number | undefined)}
            checked={props.type === 'checkbox' ? (value as boolean | undefined) : undefined}
            required={required}
            aria-invalid={error}
            aria-describedby={descriptionId}
            error={error}
            large={large}
            fullWidth={fullWidth}
            narrowWidth={narrowWidth}
            $hideSpinButtons={hideSpinButtons}
            $accentColour={accentColour}
            {...props}
        />
    );
});

export default Input;
